'use client';
import React from 'react';

interface Question {
  id: number;
  passage: number;
  type: string;
}

interface QuestionNavigatorProps {
  questions: Question[];
  currentQuestion: number;
  answers: Record<number, string>;
  flagged: Set<number>;
  onSelect: (id: number) => void;
}

const PASSAGE_RANGES = [
  { id: 'nav-p1', label: 'Passage 1', start: 1, end: 13 },
  { id: 'nav-p2', label: 'Passage 2', start: 14, end: 26 },
  { id: 'nav-p3', label: 'Passage 3', start: 27, end: 40 },
];

export default function QuestionNavigator({
  questions, currentQuestion, answers, flagged, onSelect,
}: QuestionNavigatorProps) {
  const answered = Object.keys(answers).length;
  const total = questions.length;
  const pct = Math.round((answered / total) * 100);

  return (
    <div className="w-52 flex-shrink-0 border-l border-border bg-muted/20 flex flex-col min-h-0">
      <div className="flex-shrink-0 px-3 py-2.5 border-b border-border">
        <p className="text-xs font-semibold text-foreground mb-1.5">Question Navigator</p>
        <div className="flex items-center gap-2 mb-1">
          <div className="flex-1 h-1.5 bg-border rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-xs font-semibold tabular-nums text-foreground">{answered}/{total}</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-primary inline-block" />
            <span>Answered</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-warning-bg border border-warning inline-block" />
            <span>Flagged</span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin p-3 space-y-4">
        {PASSAGE_RANGES.map((range) => {
          const passageQs = questions.filter((q) => q.id >= range.start && q.id <= range.end);
          const passageAnswered = passageQs.filter((q) => answers[q.id]).length;
          return (
            <div key={range.id}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-foreground">{range.label}</p>
                <span className="text-xs text-muted-foreground tabular-nums">{passageAnswered}/{passageQs.length}</span>
              </div>
              <div className="grid grid-cols-5 gap-1">
                {passageQs.map((q) => {
                  const isAnswered = !!answers[q.id];
                  const isFlagged = flagged.has(q.id);
                  const isCurrent = currentQuestion === q.id;
                  let btnClass = 'w-full aspect-square rounded text-xs font-semibold transition-all duration-150 border ';
                  if (isCurrent) btnClass += 'question-nav-current bg-card ';
                  else if (isFlagged) btnClass += 'question-nav-flagged ';
                  else if (isAnswered) btnClass += 'question-nav-answered ';
                  else btnClass += 'border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-primary ';
                  return (
                    <button
                      key={`qnav-${q.id}`}
                      onClick={() => onSelect(q.id)}
                      className={btnClass}
                      aria-label={`Go to question ${q.id}`}
                    >
                      {q.id}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex-shrink-0 p-3 border-t border-border space-y-1.5">
        <div className="text-xs space-y-1">
          {[
            { id: 'leg-unanswered', color: 'bg-card border border-border', label: 'Unanswered' },
            { id: 'leg-answered', color: 'bg-primary', label: 'Answered' },
            { id: 'leg-flagged', color: 'bg-warning-bg border border-warning', label: 'Flagged for review' },
            { id: 'leg-current', color: 'bg-card border-2 border-primary', label: 'Current question' },
          ].map((leg) => (
            <div key={leg.id} className="flex items-center gap-2">
              <span className={`w-3.5 h-3.5 rounded flex-shrink-0 ${leg.color}`} />
              <span className="text-muted-foreground">{leg.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}